import {
  EStreamTradeDTO,
  EStreamTradeHistoryDTO,
  formatDateDiff,
  NbLogger,
  uniqueArrayByReduceFn,
} from '@eih-netbroker/common';
import {
  BETFillList,
  BETFillListItem,
  BETFillListItemWithoutChangeDirection,
  BETFillListLastPrices,
} from '../../pages/BETFillListContext';
import { BETFillListItemChange } from '../../states/betTradesAtom';

const isBETFillListItem = (
  item: BETFillListItemWithoutChangeDirection | null,
): item is BETFillListItemWithoutChangeDirection => item !== null;

const mapListItem = (item: EStreamTradeHistoryDTO): BETFillListItemWithoutChangeDirection | null => {
  if (!item.fields.TRDPRC_1) {
    return null;
  }
  return {
    id: `${item.ticker}_${item.fields.TRADE_ID}`,
    ticker: item.ticker,
    price: item.fields.TRDPRC_1,
    quantity: item.fields.TRDVOL_1,
    time: new Date(`${item.date}T${item.fields.TIMACT_NS}Z`),
    tradeId: item.fields.TRADE_ID || '',
  };
};

const sortBETTrade = (a: BETFillListItemWithoutChangeDirection, b: BETFillListItemWithoutChangeDirection) => {
  if (a.time.getTime() === b.time.getTime()) {
    return a.tradeId.localeCompare(b.tradeId);
  }
  return a.time.getTime() - b.time.getTime();
};

const recalculateDirections =
  (lastPrices: BETFillListLastPrices) => (acc: Array<BETFillListItem>, item: BETFillListItemWithoutChangeDirection) => {
    const lastPriceOfInstrument = lastPrices[item.ticker] ?? item.price;
    if (lastPrices[item.ticker] === undefined) {
      lastPrices[item.ticker] = item.price;
    }
    const change =
      item.price === lastPrices[item.ticker]
        ? BETFillListItemChange.Neutral
        : item.price > lastPriceOfInstrument
        ? BETFillListItemChange.Up
        : BETFillListItemChange.Down;

    lastPrices[item.ticker] = item.price;
    acc.unshift({ ...item, change });
    return acc;
  };

export const mapEStreamTradeToBETFillListItem = (
  list: Array<EStreamTradeDTO>,
): Array<BETFillListItemWithoutChangeDirection> => {
  const mappedList = list
    .filter(trade => trade.trade)
    .map(
      item =>
        ({
          ticker: item.ticker,
          date: item.timestamp.split(' ')[0],

          fields: {
            TRADE_ID: item.data!.TRADE_ID,
            TRDPRC_1: item.data?.TRDPRC_1,
            TRDVOL_1: item.data?.TRDVOL_1,
            TIMACT_NS: item.data?.TIMACT_NS || '',
          },
        }) as EStreamTradeHistoryDTO,
    )
    .map(mapListItem)
    .filter(isBETFillListItem)
    .reduce(uniqueArrayByReduceFn('id'), [] as Array<BETFillListItem>)
    .toSorted((a, b) => a.time.getTime() - b.time.getTime());

  if (mappedList.length === 0) {
    return [];
  }
  return mappedList;
};

export const addNewTrades = (
  prevState: BETFillList,
  newTrades: Array<BETFillListItemWithoutChangeDirection>,
  tradeIds: Set<string>,
  lastPrices: BETFillListLastPrices,
  logger: NbLogger,
): BETFillList => {
  const validatedNewTrades = newTrades
    .filter(trade => {
      if (tradeIds.has(trade.id)) {
        return false;
      }
      tradeIds.add(trade.id);
      return true;
    })
    .reduce(recalculateDirections(lastPrices), [])
    .toSorted(sortBETTrade);
  if (validatedNewTrades.length === 0) {
    logger.warn(
      `${new Date().toISOString()} Received an old trade: ${newTrades[0].id} ${newTrades[0].time}, age=${formatDateDiff(
        new Date(),
        newTrades[0].time,
      )}`,
    );
    return prevState;
  }

  const trades = !prevState.isLoading ? [...validatedNewTrades, ...prevState.trades] : [];
  return {
    isLoading: false,
    trades,
    lastReload: prevState.isLoading ? Date.now() : prevState.lastReload,
  };
};

export const mapTradeHistoryListToBETFillList = (
  list: Array<EStreamTradeHistoryDTO>,
  lastPrices: BETFillListLastPrices,
): Array<BETFillListItem> => {
  return list
    .map(mapListItem)
    .filter(isBETFillListItem)
    .reduce(uniqueArrayByReduceFn('id'), [] as Array<BETFillListItem>)
    .toSorted(sortBETTrade)
    .reduce(recalculateDirections(lastPrices), [] as Array<BETFillListItem>);
};
